import {
  Fragment, createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState,
  type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from './Icon.js';
import { useSwipeToDismiss } from './swipe-dismiss.js';
import { useIsMobile } from './use-media.js';

// Menus (03-components §5.1, §15.2): anchored, flipping to stay in view, arrow keys, Home/End,
// type-ahead, Enter activates, Escape closes and returns focus to the trigger, click-outside
// closes, never opens on hover, 44px items on touch, bottom sheet on phones. Positioned `fixed`
// through a portal so a menu inside a scrolling table is not clipped by its container.

const MENU_ITEM_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"])';

const MenuCloseContext = createContext<() => void>(() => {});

/** Closes the nearest open menu; item components call it after their own action. */
export function useCloseMenu(): () => void {
  return useContext(MenuCloseContext);
}

export interface MenuTriggerProps {
  ref: (element: HTMLElement | null) => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string;
  onClick: (event: ReactMouseEvent<HTMLElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
}

export interface MenuState {
  open: boolean;
  setOpen: (open: boolean) => void;
  close: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  popoverId: string;
  triggerProps: MenuTriggerProps;
  /** Which end to focus when the popover opens; set by the key that opened it. */
  initialFocus: 'first' | 'last';
}

/** The trigger half of a menu: wires aria and the opening keys; pass `triggerProps` to a button. */
export function useMenu(): MenuState {
  const [open, setOpenState] = useState(false);
  const [initialFocus, setInitialFocus] = useState<'first' | 'last'>('first');
  const triggerRef = useRef<HTMLElement | null>(null);
  const popoverId = useId();

  const setOpen = useCallback((next: boolean) => setOpenState(next), []);
  const close = useCallback(() => setOpenState(false), []);

  const triggerProps: MenuTriggerProps = {
    ref: (element) => {
      triggerRef.current = element;
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': popoverId,
    onClick: () => {
      setInitialFocus('first');
      setOpenState((value) => !value);
    },
    onKeyDown: (event) => {
      if (event.nativeEvent.isComposing) return;
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setInitialFocus('first');
        setOpenState(true);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setInitialFocus('last');
        setOpenState(true);
      }
    },
  };

  return { open, setOpen, close, triggerRef, popoverId, triggerProps, initialFocus };
}

export interface MenuPopoverProps {
  menu: MenuState;
  children: ReactNode;
  /** Horizontal alignment against the trigger on desktop. */
  align?: 'start' | 'end';
  /** Accessible name for the popover region (the sheet's heading on mobile). */
  label: string;
  /** Present a bottom sheet below --bp-md (the default) or keep the anchored popover. */
  sheetOnMobile?: boolean;
  className?: string;
  /** Minimum width on desktop, a CSS length. */
  minWidth?: string;
}

interface Position {
  top: number;
  left: number;
  placement: 'top' | 'bottom';
}

/**
 * The popover half. Renders `children` — which must contain one `role="menu"` element with the
 * items — in a portal, and owns keyboard navigation, outside-click and Escape.
 */
export function MenuPopover({ menu, children, align = 'end', label, sheetOnMobile = true, className, minWidth = '14rem' }: MenuPopoverProps) {
  const { open, close, triggerRef, popoverId, initialFocus } = menu;
  const isMobile = useIsMobile();
  const asSheet = isMobile && sheetOnMobile;
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const typeahead = useRef<{ text: string; at: number }>({ text: '', at: 0 });

  const closeAndRestore = useCallback(() => {
    close();
    const trigger = triggerRef.current;
    if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
  }, [close, triggerRef]);

  // Why the sheet's handle answers a drag (03-components §4.1, swipe-to-dismiss beside — never instead of — the scrim and
  // Escape; Phase 1c finding G10): a swipe is the scrim tap's close, focus going back to the trigger.
  useSwipeToDismiss(popoverRef, open && asSheet, closeAndRestore);

  const items = useCallback((): HTMLElement[] => {
    const root = popoverRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR));
  }, []);

  // Position against the trigger; re-measure on resize and scroll so it follows the page.
  useLayoutEffect(() => {
    if (!open || asSheet) {
      setPosition(null);
      return;
    }
    const compute = (): void => {
      const trigger = triggerRef.current;
      const popover = popoverRef.current;
      if (!trigger || !popover) return;
      const rect = trigger.getBoundingClientRect();
      const width = popover.offsetWidth;
      const height = popover.offsetHeight;
      const gap = 4;
      const margin = 8;
      const fitsBelow = rect.bottom + gap + height <= window.innerHeight - margin;
      const fitsAbove = rect.top - gap - height >= margin;
      const placement: 'top' | 'bottom' = fitsBelow || !fitsAbove ? 'bottom' : 'top';
      const top = placement === 'bottom' ? rect.bottom + gap : rect.top - gap - height;
      let left = align === 'end' ? rect.right - width : rect.left;
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
      setPosition({ top: Math.max(margin, top), left, placement });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, asSheet, align, triggerRef]);

  // Focus the first/last item on open; restore to the trigger on close.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      const list = items();
      const target = initialFocus === 'last' ? list[list.length - 1] : list[0];
      (target ?? popoverRef.current)?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, initialFocus, items]);

  // Outside click closes. Pointerdown, so a click that starts inside and ends outside does not.
  // Why focus goes back to the trigger only when it fell to the page (the stage 6 review; 01-foundations §7.2: menus "MUST
  // restore [focus] to the element that opened them on close"): a press on another control takes the focus there, which
  // is the reader's own move; a press on nothing focusable would leave it on the page's body, the item it was on gone.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node | null;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close();
      requestAnimationFrame(() => {
        const active = document.activeElement;
        const trigger = triggerRef.current;
        if ((active === null || active === document.body) && trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
      });
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, close, triggerRef]);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.nativeEvent.isComposing) return;
    const list = items();
    const index = list.indexOf(document.activeElement as HTMLElement);
    const focusAt = (i: number): void => {
      const target = list[(i + list.length) % list.length];
      target?.focus({ preventScroll: true });
    };
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        closeAndRestore();
        return;
      case 'ArrowDown':
        event.preventDefault();
        focusAt(index + 1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(index - 1);
        return;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        return;
      case 'End':
        event.preventDefault();
        focusAt(list.length - 1);
        return;
      case 'Tab':
        // Why: a menu is not a form; Tab leaves it, and leaving closes it (APG menu button pattern). Why focus goes back to
        // the trigger first, and the key is NOT prevented (the stage 6 review; 01-foundations §7.2): the popover is portalled
        // to the end of the document, so the browser's own Tab from inside it left the page — or, with Shift, landed at the
        // foot of the content. From the trigger, the default Tab that follows moves to the control after it (Shift: before
        // it), where a keyboard reader expects to go from the menu's button.
        closeAndRestore();
        return;
      default:
        break;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now();
      const state = typeahead.current;
      const text = now - state.at < 500 ? state.text + event.key.toLowerCase() : event.key.toLowerCase();
      typeahead.current = { text, at: now };
      const start = index + (text.length === 1 ? 1 : 0);
      for (let step = 0; step < list.length; step += 1) {
        const candidate = list[(start + step) % list.length];
        if (candidate && (candidate.textContent ?? '').trim().toLowerCase().startsWith(text)) {
          candidate.focus({ preventScroll: true });
          break;
        }
      }
    }
  };

  if (!open || typeof document === 'undefined') return null;

  const popover = (
    <div
      id={popoverId}
      ref={popoverRef}
      className={['menu-popover', asSheet ? 'menu-popover--sheet' : 'menu-popover--anchored', position ? `is-${position.placement}` : '', className ?? ''].filter(Boolean).join(' ')}
      style={asSheet ? undefined : { top: position?.top ?? -9999, left: position?.left ?? -9999, minInlineSize: minWidth, visibility: position ? 'visible' : 'hidden' }}
      onKeyDown={onKeyDown}
      tabIndex={-1}
      aria-label={label}
    >
      {asSheet ? <div className="menu-popover__handle" aria-hidden="true" data-swipe-zone="" /> : null}
      <MenuCloseContext.Provider value={closeAndRestore}>{children}</MenuCloseContext.Provider>
    </div>
  );

  return createPortal(
    <>
      {/* Why the scrim restores too (01-foundations §7.2): a tap on it is a close, and the sheet's items go with it. */}
      {asSheet ? <div className="menu-scrim" onClick={closeAndRestore} aria-hidden="true" /> : null}
      {popover}
    </>,
    document.body,
  );
}

export interface MenuItemProps {
  children: ReactNode;
  icon?: IconName;
  /** Runs, then the menu closes. */
  onSelect?: () => void;
  /** Router destination: the item is a real link. */
  to?: string;
  /** Full navigation (server routes). */
  href?: string;
  danger?: boolean;
  disabled?: boolean;
  /** Why the item is disabled; shown beneath the label and read as the description. */
  disabledReason?: string;
  description?: string;
  /**
   * A router item that is the page the reader is on (`aria-current="page"`, and a weight and wash beside it). Why: the
   * shell's More menu holds destinations (02-patterns §1.4), and the one the reader is on is marked there as on the bar —
   * never by colour alone.
   */
  current?: boolean;
  className?: string;
}

/** One actionable row inside a `role="menu"` container. Destructive items go last after a rule. */
export function MenuItem({ children, icon, onSelect, to, href, danger = false, disabled = false, disabledReason, description, current = false, className }: MenuItemProps) {
  const closeMenu = useCloseMenu();
  const id = useId();
  const detail = disabled && disabledReason ? disabledReason : description;
  const detailId = detail ? `${id}-detail` : undefined;
  const classes = ['menu-item', danger ? 'menu-item--danger' : '', disabled ? 'is-disabled' : '', current ? 'is-current' : '', className ?? ''].filter(Boolean).join(' ');
  const body = (
    <>
      {icon ? <Icon name={icon} size={18} className="menu-item__icon" /> : null}
      <span className="menu-item__text">
        <span className="menu-item__label">{children}</span>
        {detail ? <span id={detailId} className="menu-item__detail">{detail}</span> : null}
      </span>
    </>
  );
  const shared = { role: 'menuitem' as const, tabIndex: -1, className: classes, 'aria-describedby': detailId, 'aria-disabled': disabled || undefined };
  if (disabled) {
    return <span {...shared}>{body}</span>;
  }
  if (to !== undefined) {
    return (
      <Link {...shared} to={to} aria-current={current ? 'page' : undefined} onClick={() => { onSelect?.(); closeMenu(); }}>
        {body}
      </Link>
    );
  }
  if (href !== undefined) {
    return (
      <a {...shared} href={href} onClick={() => { onSelect?.(); closeMenu(); }}>
        {body}
      </a>
    );
  }
  return (
    <button {...shared} type="button" onClick={() => { onSelect?.(); closeMenu(); }}>
      {body}
    </button>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="menu-separator" />;
}

export function MenuHeading({ children }: { children: ReactNode }) {
  return <div className="menu-heading" role="presentation">{children}</div>;
}

export type MenuEntry =
  | ({ kind?: 'item'; id: string } & Omit<MenuItemProps, 'children'> & { label: ReactNode })
  | { kind: 'separator'; id: string }
  | { kind: 'heading'; id: string; label: ReactNode };

export interface MenuProps {
  /** Accessible name of the menu, e.g. "Actions for Weekly sync". */
  label: string;
  entries: readonly MenuEntry[];
  /** Render the trigger with the wiring props spread onto a real button. */
  trigger: (props: MenuTriggerProps, state: { open: boolean }) => ReactNode;
  align?: 'start' | 'end';
  minWidth?: string;
}

/** The common case: a trigger plus a flat list of entries (row actions, overflow menus). */
export function Menu({ label, entries, trigger, align = 'end', minWidth }: MenuProps) {
  const menu = useMenu();
  const dangerIndex = entries.findIndex((entry) => entry.kind !== 'separator' && entry.kind !== 'heading' && entry.danger);
  const needsRule = dangerIndex > 0 && entries[dangerIndex - 1]?.kind !== 'separator';
  return (
    <>
      {trigger(menu.triggerProps, { open: menu.open })}
      <MenuPopover menu={menu} label={label} align={align} minWidth={minWidth}>
        <div role="menu" aria-label={label} className="menu">
          {entries.map((entry, index) => {
            if (entry.kind === 'separator') return <MenuSeparator key={entry.id} />;
            if (entry.kind === 'heading') return <MenuHeading key={entry.id}>{entry.label}</MenuHeading>;
            const { id, label: text, kind: _kind, ...rest } = entry;
            return (
              <Fragment key={id}>
                {needsRule && index === dangerIndex ? <MenuSeparator /> : null}
                <MenuItem {...rest}>{text}</MenuItem>
              </Fragment>
            );
          })}
        </div>
      </MenuPopover>
    </>
  );
}
